/**
 * Doable Tracker - Lightweight client-side analytics with GDPR cookie consent.
 * Target: ~5KB minified, ~2KB gzipped.
 * Version: 0.5.0 (CR-044: per-language consent banner selection from <html lang>).
 */

interface DoableConfig {
  trackingId?: string;
  apiKey?: string;
  endpoint?: string;
  // Config base (derived from endpoint) — set internally; override for testing.
  apiBase?: string;
  respectDNT?: boolean;
  trackPageviews?: boolean;
  trackSPA?: boolean;
  debug?: boolean;
  // CR-018: consent options — init-time overrides server config.
  requireConsent?: boolean;
  consentText?: string;
  privacyUrl?: string;
  hideRevokeButton?: boolean;
}

interface ServerConfig {
  require_consent: boolean;
  consent_text: string;
  privacy_url: string | null;
  // CR-044: per-language overrides. Keys are 2-letter language subtags
  // (e.g. {"en": "...", "sv": "..."}); resolved client-side against the
  // page's <html lang> so the 1 h config cache stays intact.
  consent_text_i18n?: Record<string, string> | null;
  privacy_url_i18n?: Record<string, string> | null;
  anonymize_ip?: boolean;
  respect_dnt?: boolean;
}

interface TrackingEvent {
  url: string;
  path: string;
  title: string;
  referrer: string;
  user_agent: string;
  screen_width: number;
  screen_height: number;
  viewport_width: number;
  viewport_height: number;
  language: string;
  timezone: string;
  session_id: string;
  timestamp: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  fbclid?: string;
  gclid?: string;
  page_load_time?: number;
}

const SESSION_KEY = 'dt_sid';
const CONSENT_COOKIE = 'dt_consent';
const REVOKE_DISMISS_KEY = 'dt_rev_dismissed';
const DEFAULT_CONSENT_TEXT = 'This site uses analytics to improve your experience.';
const ENDPOINT_DEFAULT = 'https://doable.services/api/v1/track';
const API_BASE_DEFAULT = 'https://doable.services/api/v1';

// CR-027: UTM attribution persistence.
// Stash UTMs from the landing URL into localStorage so subsequent same-site
// navigation (with no UTMs in the URL) still carries the attribution. The
// 30-minute window matches GA4 / Matomo / Plausible last-non-direct-click
// defaults: any fresh UTM arriving within that window replaces the stored
// entry (last-touch wins), and when there's no UTM in the URL, the stored
// entry survives until it ages past the window.
const UTM_STORAGE_KEY = 'doable.utm';
const UTM_WINDOW_MS = 30 * 60 * 1000;  // 30 minutes
const UTM_KEYS: readonly string[] = [
  'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
];

// CR-027: PII-field rejection. Custom event payloads (passed via
// doable('track', <name>, <data>)) are filtered to strip keys that look
// like personally-identifying information. The server-side ingestion
// handler runs the same regex as a belt-and-braces defence, but stopping
// the data at the browser layer is the primary guardrail — it never
// leaves the visitor's device.
//
// Keep this pattern in sync with tracking_api.PII_KEY_PATTERN on the
// server.
const PII_KEY_RE = /^(email|e.?mail|phone|tel|mobile|address|postal|zip|ssn|personnummer|birth(day|date|dob)|passport|iban|card|cvv|password|pwd|token)$/i;

let config: DoableConfig = {};
let serverConfig: ServerConfig | null = null;
let effectiveConfig: ServerConfig | null = null;
let sessionId: string = '';
let initialized = false;
let trackingEnabled = false;  // becomes true only after consent (or when consent not required)
let pendingInitialPageview = false;
let bannerEl: HTMLElement | null = null;
let revokeEl: HTMLElement | null = null;

function deriveApiBase(endpoint: string): string {
  // Strip trailing path segment (typically "/track") to get API base.
  try {
    const idx = endpoint.lastIndexOf('/');
    if (idx > 0) return endpoint.substring(0, idx);
  } catch (e) {}
  return API_BASE_DEFAULT;
}

function generateSessionId(): string {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let id = '';
  for (let i = 0; i < 16; i++) {
    id += chars[Math.floor(Math.random() * chars.length)];
  }
  return id;
}

function getSessionId(): string {
  try {
    let sid = sessionStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid = generateSessionId();
      sessionStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  } catch (e) {
    return generateSessionId();
  }
}

function clearSessionId(): void {
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch (e) {}
  sessionId = '';
}

function readCookie(name: string): string | null {
  try {
    const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : null;
  } catch (e) {
    return null;
  }
}

function writeCookie(name: string, value: string, maxAgeSeconds: number): void {
  try {
    const secure = location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = name + '=' + encodeURIComponent(value) +
      '; Path=/; Max-Age=' + maxAgeSeconds + '; SameSite=Lax' + secure;
  } catch (e) {}
}

function deleteCookie(name: string): void {
  try {
    const secure = location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = name + '=; Path=/; Max-Age=0; SameSite=Lax' + secure;
  } catch (e) {}
}

function getUrlParam(name: string): string | undefined {
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get(name) || undefined;
  } catch (e) {
    return undefined;
  }
}

// ---- CR-027: UTM persistence --------------------------------------------

interface StoredUtms {
  ts: number;                      // epoch ms when stored
  values: Record<string, string>;  // { utm_source: "...", ... }
}

/** Read UTM values from window.location.search. Returns only the keys
 *  that are actually present (non-empty). */
function readUtmsFromUrl(): Record<string, string> {
  const out: Record<string, string> = {};
  try {
    const params = new URLSearchParams(window.location.search);
    for (const k of UTM_KEYS) {
      const v = params.get(k);
      if (v) out[k] = v;
    }
  } catch (e) {}
  return out;
}

/** Load the previously-persisted UTMs from localStorage, or null. */
function loadStoredUtms(): StoredUtms | null {
  try {
    const raw = localStorage.getItem(UTM_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object'
        && typeof parsed.ts === 'number'
        && parsed.values && typeof parsed.values === 'object') {
      return parsed as StoredUtms;
    }
  } catch (e) {}
  return null;
}

function saveStoredUtms(values: Record<string, string>): void {
  try {
    const payload: StoredUtms = { ts: Date.now(), values };
    localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(payload));
  } catch (e) {}
}

/** Rule from CR-027 spec:
 *  - URL has UTMs          → overwrite stored entry (last-touch wins)
 *  - URL has no UTMs
 *       stored < 30 min    → keep stored entry for this session
 *       stored ≥ 30 min    → stored entry stale; drop it
 *
 *  Called once on init after the server config load. */
function reconcileUtmPersistence(): void {
  const fromUrl = readUtmsFromUrl();
  if (Object.keys(fromUrl).length > 0) {
    // Last-touch: fresh UTMs from the URL replace whatever was stored.
    saveStoredUtms(fromUrl);
    return;
  }
  const stored = loadStoredUtms();
  if (stored && (Date.now() - stored.ts) > UTM_WINDOW_MS) {
    try { localStorage.removeItem(UTM_STORAGE_KEY); } catch (e) {}
  }
}

/** Merge persisted UTMs into an event — only filling in keys that aren't
 *  already present (URL > stored; URL always wins). */
function hydrateUtmsIntoEvent(event: TrackingEvent): void {
  const stored = loadStoredUtms();
  if (!stored) return;
  for (const k of UTM_KEYS) {
    if (!(event as any)[k] && stored.values[k]) {
      (event as any)[k] = stored.values[k];
    }
  }
}

// ---- CR-027: PII filter -------------------------------------------------

/** Strip keys matching PII_KEY_RE from the data blob passed to track().
 *  Returns the cleaned object and the list of dropped keys for logging. */
function stripPiiKeys(
  data: Record<string, any> | undefined
): { cleaned: Record<string, any> | undefined; dropped: string[] } {
  if (!data) return { cleaned: data, dropped: [] };
  const cleaned: Record<string, any> = {};
  const dropped: string[] = [];
  for (const k of Object.keys(data)) {
    if (PII_KEY_RE.test(k)) {
      dropped.push(k);
    } else {
      cleaned[k] = data[k];
    }
  }
  return { cleaned, dropped };
}

function isDNTEnabled(): boolean {
  return navigator.doNotTrack === '1' ||
         (window as any).doNotTrack === '1' ||
         (navigator as any).msDoNotTrack === '1';
}

function getTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (e) {
    return '';
  }
}

async function sha256Hex(input: string): Promise<string> {
  try {
    if (window.crypto && window.crypto.subtle) {
      const buf = new TextEncoder().encode(input);
      const hashBuf = await window.crypto.subtle.digest('SHA-256', buf);
      const arr = Array.from(new Uint8Array(hashBuf));
      return arr.map(b => b.toString(16).padStart(2, '0')).join('');
    }
  } catch (e) {}
  return '';
}

function collectEventData(): TrackingEvent {
  const now = new Date();
  const perf = window.performance;
  let pageLoadTime: number | undefined;

  if (perf && perf.timing) {
    const timing = perf.timing;
    if (timing.loadEventEnd > 0 && timing.navigationStart > 0) {
      pageLoadTime = timing.loadEventEnd - timing.navigationStart;
    }
  }

  const event: TrackingEvent = {
    url: window.location.href,
    path: window.location.pathname,
    title: document.title,
    referrer: document.referrer,
    user_agent: navigator.userAgent,
    screen_width: window.screen.width,
    screen_height: window.screen.height,
    viewport_width: window.innerWidth,
    viewport_height: window.innerHeight,
    language: navigator.language,
    timezone: getTimezone(),
    session_id: sessionId,
    timestamp: now.toISOString(),
    utm_source: getUrlParam('utm_source'),
    utm_medium: getUrlParam('utm_medium'),
    utm_campaign: getUrlParam('utm_campaign'),
    utm_term: getUrlParam('utm_term'),
    utm_content: getUrlParam('utm_content'),
    fbclid: getUrlParam('fbclid'),
    gclid: getUrlParam('gclid'),
    page_load_time: pageLoadTime
  };

  // CR-027: if the current URL didn't carry UTMs, try to hydrate from
  // persisted last-touch values (set on the landing page with ?utm_*=...).
  // Keeps attribution intact across same-site navigation.
  hydrateUtmsIntoEvent(event);
  return event;
}

function sendEvent(event: TrackingEvent): void {
  const endpoint = config.endpoint || ENDPOINT_DEFAULT;

  const payload = JSON.stringify({
    api_key: config.apiKey,
    tracking_id: config.trackingId,
    events: [event]
  });

  if (config.debug) {
    console.log('[Doable] Sending event:', event);
  }

  if (navigator.sendBeacon) {
    try {
      const blob = new Blob([payload], { type: 'text/plain' });
      const success = navigator.sendBeacon(endpoint, blob);
      if (success) return;
    } catch (e) {}
  }

  try {
    fetch(endpoint, {
      method: 'POST',
      body: payload,
      keepalive: true
    }).catch(() => {});
  } catch (e) {}
}

function trackPageview(): void {
  if (!initialized || !trackingEnabled) return;
  if (config.respectDNT && isDNTEnabled()) return;
  if (!sessionId) sessionId = getSessionId();
  sendEvent(collectEventData());
}

function sendConsentAudit(): void {
  if (!effectiveConfig || !config.trackingId) return;
  const apiBase = config.apiBase || deriveApiBase(config.endpoint || ENDPOINT_DEFAULT);
  const textShown = effectiveConfig.consent_text || DEFAULT_CONSENT_TEXT;
  sha256Hex(textShown).then((textHash) => {
    sha256Hex(sessionId || '').then((sidHash) => {
      const payload = JSON.stringify({
        tracking_id: config.trackingId,
        session_id_hash: sidHash,
        consent_text_hash: textHash,
        privacy_url: effectiveConfig ? effectiveConfig.privacy_url : null,
      });
      const url = apiBase + '/track/consent';
      try {
        if (navigator.sendBeacon) {
          const blob = new Blob([payload], { type: 'text/plain' });
          if (navigator.sendBeacon(url, blob)) return;
        }
      } catch (e) {}
      try {
        fetch(url, { method: 'POST', body: payload, keepalive: true }).catch(() => {});
      } catch (e) {}
    });
  });
}

function fetchServerConfig(): Promise<ServerConfig | null> {
  const apiBase = config.apiBase || deriveApiBase(config.endpoint || ENDPOINT_DEFAULT);
  const url = apiBase + '/track/config/' + encodeURIComponent(config.trackingId || '');
  return fetch(url, { credentials: 'omit' })
    .then((r) => {
      if (!r.ok) return null;
      return r.json();
    })
    .catch(() => null);
}

// CR-044: pick the page's language from <html lang> first (the right signal
// for "this is the EN page"), then the browser preference. Lower-cased,
// primary subtag only.
const detectLang = (): string =>
  (document.documentElement.getAttribute('lang')
   || navigator.language || '').slice(0, 2).toLowerCase();

function mergeConfig(server: ServerConfig | null): ServerConfig {
  // Init-time overrides server config.
  const requireConsent = typeof config.requireConsent === 'boolean'
    ? config.requireConsent
    : (server ? !!server.require_consent : false);

  // CR-044: resolve consent_text / privacy_url by priority (highest first):
  //   1. config.consentText / .privacyUrl — explicit init override
  //   2. server.<field>_i18n[lang] — per-language override
  //   3. server.<scalar field> — project default
  //   4. DEFAULT_CONSENT_TEXT / null
  const lang = detectLang();
  const ctMap = server && server.consent_text_i18n;
  const puMap = server && server.privacy_url_i18n;
  const ctLang = ctMap && lang && ctMap[lang];
  const puLang = puMap && lang && puMap[lang];

  const consentText = config.consentText
    || ctLang
    || (server && server.consent_text) || DEFAULT_CONSENT_TEXT;
  const privacyUrl = config.privacyUrl
    || puLang
    || (server && server.privacy_url) || null;
  return {
    require_consent: requireConsent,
    consent_text: consentText,
    privacy_url: privacyUrl,
    anonymize_ip: server ? server.anonymize_ip : true,
    respect_dnt: server ? server.respect_dnt : true,
  };
}

// ---- Banner UI ----------------------------------------------------------

function removeBanner(): void {
  if (bannerEl && bannerEl.parentNode) {
    bannerEl.parentNode.removeChild(bannerEl);
  }
  bannerEl = null;
}

function renderBanner(): void {
  if (!effectiveConfig) return;
  removeBanner();

  const wrap = document.createElement('div');
  wrap.setAttribute('data-dt-banner', '');
  wrap.style.cssText = [
    'position:fixed', 'left:0', 'right:0', 'bottom:0', 'z-index:2147483646',
    'background:#1f2430', 'color:#f5f7fa', 'font:13px/1.5 -apple-system,BlinkMacSystemFont,Segoe UI,Roboto,Helvetica,Arial,sans-serif',
    'padding:14px 18px', 'box-shadow:0 -4px 18px rgba(0,0,0,.25)',
    'display:flex', 'flex-wrap:wrap', 'align-items:center', 'gap:12px',
    'box-sizing:border-box'
  ].join(';');

  const msg = document.createElement('div');
  msg.style.cssText = 'flex:1 1 240px; min-width:220px;';
  msg.textContent = effectiveConfig.consent_text || DEFAULT_CONSENT_TEXT;
  if (effectiveConfig.privacy_url) {
    const sep = document.createElement('span');
    sep.textContent = ' ';
    msg.appendChild(sep);
    const link = document.createElement('a');
    link.href = effectiveConfig.privacy_url;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.textContent = 'Privacy policy';
    link.style.cssText = 'color:#7aa2f7;text-decoration:underline;';
    msg.appendChild(link);
  }
  wrap.appendChild(msg);

  const btnStyle = 'padding:8px 16px; border:1px solid rgba(255,255,255,.25); background:#2d3344; color:#f5f7fa; border-radius:6px; font:inherit; cursor:pointer; min-width:90px;';

  const decline = document.createElement('button');
  decline.type = 'button';
  decline.textContent = 'Decline';
  decline.style.cssText = btnStyle;
  decline.onclick = () => {
    writeCookie(CONSENT_COOKIE, 'denied', 31536000);
    removeBanner();
    clearSessionId();
    trackingEnabled = false;
  };
  wrap.appendChild(decline);

  const accept = document.createElement('button');
  accept.type = 'button';
  accept.textContent = 'Accept';
  accept.style.cssText = btnStyle;
  accept.onclick = () => {
    writeCookie(CONSENT_COOKIE, 'granted', 31536000);
    removeBanner();
    sessionId = getSessionId();
    trackingEnabled = true;
    sendConsentAudit();
    renderRevokeIcon();
    trackPageview();
  };
  wrap.appendChild(accept);

  (document.body || document.documentElement).appendChild(wrap);
  bannerEl = wrap;
}

// ---- Revoke icon --------------------------------------------------------

function removeRevokeIcon(): void {
  if (revokeEl && revokeEl.parentNode) {
    revokeEl.parentNode.removeChild(revokeEl);
  }
  revokeEl = null;
}

function renderRevokeIcon(): void {
  if (config.hideRevokeButton) return;
  try {
    if (sessionStorage.getItem(REVOKE_DISMISS_KEY) === '1') return;
  } catch (e) {}
  removeRevokeIcon();

  const btn = document.createElement('button');
  btn.setAttribute('type', 'button');
  btn.setAttribute('aria-label', 'Cookie consent settings');
  btn.setAttribute('title', 'Cookie consent');
  btn.setAttribute('data-dt-revoke', '');
  btn.style.cssText = [
    'position:fixed', 'left:16px', 'bottom:16px', 'z-index:2147483645',
    'width:32px', 'height:32px', 'border-radius:50%', 'border:none',
    'background:#1f2430', 'color:#f5f7fa', 'cursor:pointer',
    'opacity:.55', 'transition:opacity .15s',
    'box-shadow:0 2px 8px rgba(0,0,0,.2)',
    'display:flex', 'align-items:center', 'justify-content:center',
    'padding:0'
  ].join(';');
  btn.onmouseenter = () => { btn.style.opacity = '1'; };
  btn.onmouseleave = () => { btn.style.opacity = '.55'; };
  btn.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><circle cx="9" cy="10" r="1" fill="currentColor"/><circle cx="14" cy="8" r="1" fill="currentColor"/><circle cx="15" cy="14" r="1" fill="currentColor"/><circle cx="10" cy="15" r="1" fill="currentColor"/></svg>';

  const dismiss = document.createElement('span');
  dismiss.textContent = '×';
  dismiss.title = 'Hide for this session';
  dismiss.style.cssText = 'position:absolute;top:-6px;right:-6px;width:16px;height:16px;border-radius:50%;background:#444;color:#f5f7fa;font-size:12px;line-height:16px;text-align:center;cursor:pointer;';
  dismiss.onclick = (ev) => {
    ev.stopPropagation();
    try { sessionStorage.setItem(REVOKE_DISMISS_KEY, '1'); } catch (e) {}
    removeRevokeIcon();
  };

  btn.onclick = () => {
    renderBanner();
  };

  const container = document.createElement('div');
  container.style.cssText = 'position:fixed;left:16px;bottom:16px;z-index:2147483645;';
  container.appendChild(btn);
  container.appendChild(dismiss);
  // Adjust absolute positioning of inner button relative to container
  btn.style.position = 'static';
  btn.style.left = '';
  btn.style.bottom = '';

  (document.body || document.documentElement).appendChild(container);
  revokeEl = container;
}

// ---- Revoke command -----------------------------------------------------

function revokeConsent(): void {
  deleteCookie(CONSENT_COOKIE);
  clearSessionId();
  trackingEnabled = false;
  removeRevokeIcon();
  try { sessionStorage.removeItem(REVOKE_DISMISS_KEY); } catch (e) {}
  if (effectiveConfig && effectiveConfig.require_consent) {
    renderBanner();
  }
}

// ---- Init ---------------------------------------------------------------

function proceedWithConsentState(): void {
  if (!effectiveConfig) return;
  const state = readCookie(CONSENT_COOKIE);

  if (state === 'denied') {
    trackingEnabled = false;
    return;
  }

  if (state === 'granted') {
    sessionId = getSessionId();
    trackingEnabled = true;
    renderRevokeIcon();
    if (pendingInitialPageview) {
      pendingInitialPageview = false;
      trackPageview();
    }
    return;
  }

  // No decision yet.
  if (effectiveConfig.require_consent) {
    renderBanner();
    return;
  }

  // Consent not required — fall back to DNT behavior.
  if (config.respectDNT && isDNTEnabled()) {
    trackingEnabled = false;
    return;
  }
  sessionId = getSessionId();
  trackingEnabled = true;
  if (pendingInitialPageview) {
    pendingInitialPageview = false;
    trackPageview();
  }
}

function init(options: DoableConfig): void {
  if (initialized) {
    if (config.debug) {
      console.warn('[Doable] Already initialized');
    }
    return;
  }

  config = { ...options };
  if (!config.apiBase) {
    config.apiBase = deriveApiBase(config.endpoint || ENDPOINT_DEFAULT);
  }

  initialized = true;

  // CR-027: stash / refresh UTM persistence. Runs on every page load,
  // before any consent decision. Only non-PII attribution metadata;
  // safe to do regardless of tracking state because we're writing to
  // localStorage, not sending anything. If consent ends up declined,
  // no events fire and the stored UTMs simply expire after 30 min.
  reconcileUtmPersistence();

  if (config.debug) {
    console.log('[Doable] Initialized with config:', config);
  }

  pendingInitialPageview = config.trackPageviews !== false;

  if (config.trackSPA !== false) {
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;

    history.pushState = function(...args) {
      originalPushState.apply(this, args);
      setTimeout(trackPageview, 0);
    };

    history.replaceState = function(...args) {
      originalReplaceState.apply(this, args);
      setTimeout(trackPageview, 0);
    };

    window.addEventListener('popstate', () => {
      setTimeout(trackPageview, 0);
    });
  }

  // Fetch server config, then decide on consent state.
  fetchServerConfig().then((srv) => {
    serverConfig = srv;
    effectiveConfig = mergeConfig(srv);

    // If the server says the project is gone (null response) and no init override,
    // fail closed — do not track or render banner.
    if (!srv && typeof config.requireConsent !== 'boolean') {
      if (config.debug) console.warn('[Doable] Config fetch failed; staying silent.');
      return;
    }

    const startAfterLoad = () => {
      if (document.readyState === 'complete') {
        proceedWithConsentState();
      } else {
        window.addEventListener('load', proceedWithConsentState);
      }
    };
    startAfterLoad();
  });
}

function track(eventName?: string, data?: Record<string, any>): void {
  if (!initialized) {
    if (config.debug) {
      console.warn('[Doable] Not initialized');
    }
    return;
  }
  if (!trackingEnabled) return;
  if (config.respectDNT && isDNTEnabled()) return;
  if (!sessionId) sessionId = getSessionId();

  const event = collectEventData();
  if (eventName) {
    // Set both fields for server compatibility; server accepts either.
    (event as any).event = eventName;
    (event as any).event_name = eventName;
  }

  // CR-027: strip PII-shaped keys from the user-provided data blob
  // before merging into the event. Also warn in the console so a
  // developer instrumenting a form notices they're trying to push
  // protected fields through the tracker.
  const { cleaned, dropped } = stripPiiKeys(data);
  if (dropped.length > 0) {
    try {
      console.warn(
        '[Doable] Dropped PII-shaped key(s) from tracker event: '
        + dropped.join(', ')
        + '. Store form content in your own backend, not in tracker events.'
      );
    } catch (e) {}
  }
  if (cleaned) {
    Object.assign(event, cleaned);
  }
  sendEvent(event);
}

// Command queue processing
type DoableCommand =
  | ['init', DoableConfig]
  | ['track', string?, Record<string, any>?]
  | ['revoke'];

function processCommand(args: DoableCommand): void {
  const [cmd, ...params] = args;
  if (cmd === 'init') {
    init(params[0] as DoableConfig);
  } else if (cmd === 'track') {
    track(params[0] as string | undefined, params[1] as Record<string, any> | undefined);
  } else if (cmd === 'revoke') {
    revokeConsent();
  }
}

declare global {
  interface Window {
    doable: DoableFunction & { q?: DoableCommand[] };
  }
}

type DoableFunction = {
  (cmd: 'init', config: DoableConfig): void;
  (cmd: 'track', eventName?: string, data?: Record<string, any>): void;
  (cmd: 'revoke'): void;
};

(function() {
  const queue = window.doable?.q || [];

  // CR-042: self-init from data-* attributes on the tracker's own <script>.
  // The GA4 / Plausible / Fathom pattern — a bare
  // `<script src=".../dt.min.js" data-tracking-id="..." data-api-key="..." async>`
  // with no `doable('init', ...)` call — is the instinctive embed, so support
  // it instead of failing silently. `document.currentScript` is null for
  // async/defer scripts, so fall back to a querySelector for our own tag.
  // Explicit init still wins: if an `init` is already queued we skip synthesis.
  try {
    const scriptEl = (document.currentScript as HTMLScriptElement | null)
      || document.querySelector<HTMLScriptElement>(
           'script[data-tracking-id][src*="dt.min.js"]');
    const ds = scriptEl?.dataset;
    const hasQueuedInit = queue.some((c) => c[0] === 'init');
    if (ds?.trackingId && !hasQueuedInit) {
      queue.unshift(['init', {
        trackingId: ds.trackingId,
        apiKey: ds.apiKey,
        endpoint: ds.endpoint,
        requireConsent: ds.requireConsent === undefined
          ? undefined : ds.requireConsent === 'true',
        debug: ds.debug === 'true',
      }]);
    }
  } catch (e) {}

  const doable: DoableFunction = function(cmd: string, ...args: any[]) {
    processCommand([cmd, ...args] as DoableCommand);
  } as DoableFunction;

  window.doable = doable;

  for (const cmd of queue) {
    processCommand(cmd);
  }

  // CR-042: surface silent misconfiguration. If the script loaded but no
  // init ever ran (neither data-* nor a doable('init', ...) call), warn —
  // unconditionally, NOT gated on config.debug, because a silent no-op is
  // exactly the failure we're trying to make visible. One-shot, with a 3 s
  // post-load grace so a legitimately deferred init doesn't trip it. Never
  // throws.
  const warnIfUninit = () => {
    if (!initialized) {
      try {
        console.warn(
          '[Doable] tracker script loaded but never initialised — call '
          + "doable('init', { trackingId, apiKey }) or add data-tracking-id "
          + 'to the <script> tag. See https://doable.services/docs/quickstart');
      } catch (e) {}
    }
  };
  try {
    if (document.readyState === 'complete') {
      setTimeout(warnIfUninit, 3000);
    } else {
      window.addEventListener('load', () => setTimeout(warnIfUninit, 3000));
    }
  } catch (e) {}
})();

export { init, track, trackPageview, revokeConsent };
